// Single source of truth for the plain-text "code + name" identity labels shown
// across the app's entity pickers (item / location / currency): dropdown options,
// input text, committed-filter chips, resolve dialogs. Every helper here is
// display-only — the wire value (q / search / the option's `value`) always stays
// the raw code. Each returns '' for null/undefined so it's safe to feed straight
// into a controlled input.
//
// House separator for every "code + name" label is a spaced em-dash (LABEL_SEP).
// Change it here, once, and every picker follows.
export const LABEL_SEP = ' — ';

// On image-capable (Shopify) stores the item_code is a GID suffix the merchant
// never sees, so the label leads with the product NAME (+ SKU) instead — matching
// the grids' Product cell. Other stores keep "item_code — item_name (sku)", where
// item_code is a real, known value. SKU reads whichever wire key the picker's
// projection carries: `sku` (/api/items/compact) or `shopify_sku` (/api/items).
//
// Every part is composed only when it is actually there, and a row carrying
// nothing nameable yields '' — never a template with `undefined` in it, which
// reads to a merchant as an item genuinely called "undefined". On a Shopify
// row the fallback stops at the SKU and never reaches item_code:
// `Item.ItemName` is NOT NULL (api/models.py), so a nameless row
// is a serializer bug, and substituting the variant id would print the one
// value this whole seam exists to keep off the screen. The server half states
// the same rule in the same words (api/services/item_lookup.item_label: "one
// carrying neither returns ''") and the two must not answer differently.
export function formatItemLabel(item, isShopify = false) {
  if (!item) return '';
  const sku = item.sku ?? item.shopify_sku ?? '';
  const name = item.item_name || '';
  if (isShopify) return name && sku ? `${name} (${sku})` : (name || sku);

  const code = item.item_code || '';
  const label = code && name ? `${code}${LABEL_SEP}${name}` : (code || name);
  return sku && sku !== code && label ? `${label} (${sku})` : label;
}

// "WH-01 — Main Warehouse" — the location twin of formatItemLabel, and it
// branches for the same reason. On a Shopify store `Location.LocationCode` is
// the location GID's numeric suffix minted by the sync
// (shopify_app/services/sync.py sync_location), a number the merchant has
// never seen, so the label is the name alone. Everywhere else the code is the
// merchant's own and leads.
//
// A location known only by its code — a filter still applied after the
// locations read failed (features/item-filter/ItemFilterBar) — reads as that
// code where it is the merchant's own, and as '' on a Shopify store rather
// than as the value this branch exists to keep off the screen.
export function formatLocationLabel(loc, isShopify = false) {
  if (!loc) return '';
  const name = loc.location_name || '';
  if (isShopify) return name;
  return name ? `${loc.location_code}${LABEL_SEP}${name}` : (loc.location_code || '');
}

// "USD ($)" — ISO code plus parenthesized symbol when present. Used bare in
// compact currency cells and as the lead of the full currency label below.
export function formatCurrencyCode(ccy) {
  if (!ccy) return '';
  return `${ccy.currency_code}${ccy.symbol ? ` (${ccy.symbol})` : ''}`;
}

// "USD ($) — US Dollar" — full currency identity for dropdown options.
export function formatCurrencyLabel(ccy) {
  if (!ccy) return '';
  return `${formatCurrencyCode(ccy)}${LABEL_SEP}${ccy.currency_name}`;
}
